"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Grid3x3, Rows3, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Toggle } from "@/components/ui/toggle";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const romanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];
const galleryImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_practice_grit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_practice_grit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_practice_grit.png-wvc-sizes",
  alt: "Local band practicing in the main room",
  caption: "Main Stage · Live Tracking Session",
  year: "MMXXIV",
  orientation: "landscape"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/tube_amp_glow.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/tube_amp_glow.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/tube_amp_glow.png-wvc-sizes",
  alt: "Glowing vacuum tubes in a guitar amp",
  caption: "Vintage Heat · Custom Tube Head",
  year: "MMXXIV",
  orientation: "portrait"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/drum_kit_overhead.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/drum_kit_overhead.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/drum_kit_overhead.png-wvc-sizes",
  alt: "Overhead view of a premium drum kit",
  caption: "The Engine Room · Birch Shells",
  year: "MMXXIII",
  orientation: "portrait"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/mixing_desk_faders.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/mixing_desk_faders.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/mixing_desk_faders.png-wvc-sizes",
  alt: "Close up of mixing desk faders",
  caption: "Signal Path · Analog Summing",
  year: "MMXXIV",
  orientation: "landscape"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/vocalist_motion_blur.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/vocalist_motion_blur.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/vocalist_motion_blur.png-wvc-sizes",
  alt: "Vocalist in motion blur during performance",
  caption: "Raw Energy · Vocal Booth A",
  year: "MMXXIV",
  orientation: "landscape"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/cable_nest_detail.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/cable_nest_detail.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/cable_nest_detail.png-wvc-sizes",
  alt: "Detail of audio cables on the floor",
  caption: "The Nervous System · XLR Patching",
  year: "MMXXIII",
  orientation: "portrait"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_rack_lineup.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_rack_lineup.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_rack_lineup.png-wvc-sizes",
  alt: "Lineup of electric guitars in a rack",
  caption: "The Arsenal · Offset Collection",
  year: "MMXXIV",
  orientation: "landscape"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_meeting_couch.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_meeting_couch.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_meeting_couch.png-wvc-sizes",
  alt: "Band members discussing music on a couch",
  caption: "Pre-Production · The Green Room",
  year: "MMXXIV",
  orientation: "landscape"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_door_sign.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_door_sign.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_door_sign.png-wvc-sizes",
  alt: "Studio door with On Air sign",
  caption: "The Threshold · Recording in Progress",
  year: "MMXXIII",
  orientation: "portrait"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/pedalboard_lights.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/pedalboard_lights.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/pedalboard_lights.webp-wvc-sizes",
  alt: "Glowing LED lights on a guitar pedalboard",
  caption: "Sonic Textures · Signal Chain",
  year: "MMXXIV",
  orientation: "landscape"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/bass_player_silhouette.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/bass_player_silhouette.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/bass_player_silhouette.png-wvc-sizes",
  alt: "Silhouette of a bass player against a bright light",
  caption: "Low End Theory · Tracking Bass",
  year: "MMXXIV",
  orientation: "portrait"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/cassette_tape_stack.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/cassette_tape_stack.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/cassette_tape_stack.png-wvc-sizes",
  alt: "Stack of old cassette tapes",
  caption: "Analog Roots · The Archive",
  year: "MMXXIII",
  orientation: "landscape"
}];
export default function LiveTracking() {
  const [isGrid, setIsGrid] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || isGrid) return;
    const handleScroll = () => {
      const children = Array.from(el.children) as HTMLElement[];
      const scrollLeft = el.scrollLeft + el.clientWidth / 2;
      let closest = 0;
      let closestDist = Infinity;
      children.forEach((child, idx) => {
        const center = child.offsetLeft + child.offsetWidth / 2;
        const dist = Math.abs(center - scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = idx;
        }
      });
      setCurrentIndex(closest);
    };
    el.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isGrid]);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || isGrid) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", onWheel, {
      passive: false
    });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isGrid]);
  const openLightbox = (i: number) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex(i => i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length);
  const nextImage = () => setLightboxIndex(i => i === null ? null : (i + 1) % galleryImages.length);
  return <section data-section-id="2391" id="live-tracking" className="bg-background text-foreground relative w-full overflow-hidden">
      {}
      <div ref={titleRef} className={`relative z-10 flex flex-col gap-4 px-6 pt-12 pb-8 transition-all duration-700 md:flex-row md:items-end md:justify-between md:gap-8 md:px-12 md:pt-14 lg:px-20 ${titleVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
      minHeight: "96px"
    }}>
        <div className="flex flex-col gap-3">
          <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{
          color: "var(--primary)"
        }}>
            SECTION_02 // THE_SCENE
          </span>
          <h1 className="font-default text-4xl leading-[0.9] font-extrabold uppercase tracking-tighter md:text-5xl lg:text-[7rem]">
            Live Tracking.
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <span className="font-mono text-muted-foreground hidden text-xs uppercase tracking-widest md:inline">
            Layout
          </span>
          <div className="border-border bg-card inline-flex items-center rounded-md border p-1">
            <Toggle pressed={!isGrid} onPressedChange={() => setIsGrid(false)} size="sm" aria-label="View as scroll" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground text-foreground/70 gap-2 rounded-md font-mono text-xs uppercase">
              <Rows3 className="size-3.5" />
              <span className="hidden sm:inline">Tape</span>
            </Toggle>
            <Toggle pressed={isGrid} onPressedChange={() => setIsGrid(true)} size="sm" aria-label="View as grid" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground text-foreground/70 gap-2 rounded-md font-mono text-xs uppercase">
              <Grid3x3 className="size-3.5" />
              <span className="hidden sm:inline">Rack</span>
            </Toggle>
          </div>
        </div>
      </div>

      {}
      {!isGrid ? <>
          {}
          <div ref={scrollerRef} className="scrollbar-none hidden snap-x snap-mandatory overflow-x-auto overflow-y-hidden md:flex" style={{
        scrollbarWidth: "none"
      }}>
            <style>{`.scrollbar-none::-webkit-scrollbar{display:none;}`}</style>
            <div className="w-12 shrink-0 md:w-16 lg:w-20" aria-hidden="true" />
            {galleryImages.map((image, i) => {
          const isPortrait = image.orientation === "portrait";
          return <figure data-index={i} key={image.src} onClick={() => openLightbox(i)} className={`group flex shrink-0 cursor-pointer snap-center flex-col justify-center pr-16 ${isPortrait ? "w-[50vw] max-w-[640px]" : "w-[70vw] max-w-[1100px]"}`}>
                  <div className="relative overflow-hidden rounded-md shadow-2xl" style={{
              height: "78vh"
            }}>
                    <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02] grayscale contrast-125 hover:grayscale-0" />
                    <div className="pointer-events-none absolute inset-0" style={{
                background: "radial-gradient(ellipse at center, transparent 55%, hsla(45,90%,5%,0.45) 100%)"
              }} />
                  </div>
                  <figcaption className="mt-5 flex items-baseline justify-between gap-6 pl-1 pr-1">
                    <div className="flex items-baseline gap-4">
                      <span className="font-mono text-lg font-bold" style={{
                  color: "var(--primary)"
                }}>
                        {romanNumerals[i]}
                      </span>
                      <span className="font-default text-foreground/90 text-sm font-semibold uppercase tracking-tight md:text-base">
                        {image.caption}
                      </span>
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                      {image.year}
                    </span>
                  </figcaption>
                </figure>;
        })}
            <div className="w-12 shrink-0 md:w-16 lg:w-20" aria-hidden="true" />
          </div>

          {}
          <div className="flex flex-col gap-12 px-6 pb-16 md:hidden">
            {galleryImages.map((image, i) => <figure data-index={i} key={image.src} onClick={() => openLightbox(i)} className="cursor-pointer">
                <div className="relative overflow-hidden rounded-md shadow-xl" style={{
            height: "78vh"
          }}>
                  <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale contrast-125" />
                  <div className="pointer-events-none absolute inset-0" style={{
              background: "radial-gradient(ellipse at center, transparent 55%, hsla(45,90%,5%,0.45) 100%)"
            }} />
                </div>
                <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-base font-bold" style={{
                color: "var(--primary)"
              }}>
                      {romanNumerals[i]}
                    </span>
                    <span className="font-default text-foreground/90 text-sm font-semibold uppercase tracking-tight">
                      {image.caption}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{
              color: "var(--muted-foreground)"
            }}>
                    {image.year}
                  </span>
                </figcaption>
              </figure>)}
          </div>

          {}
          <div className="border-border bg-card/90 pointer-events-none fixed right-6 bottom-6 z-20 hidden items-center gap-2 rounded-md border px-4 py-2 shadow-2xl backdrop-blur-md md:right-12 md:bottom-10 md:flex">
            <span className="font-mono text-xl font-bold" style={{
          color: "var(--primary)"
        }}>
              {romanNumerals[currentIndex]}
            </span>
            <span className="text-muted-foreground font-mono text-base">/</span>
            <span className="text-muted-foreground font-mono text-base">
              {romanNumerals[galleryImages.length - 1]}
            </span>
          </div>
        </> : <div className="px-6 pb-20 md:px-12 lg:px-20">
          <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 xl:columns-4">
            {galleryImages.map((image, i) => <figure data-index={i} key={image.src} onClick={() => openLightbox(i)} className="group mb-6 inline-block w-full cursor-pointer break-inside-avoid">
                <div className="relative overflow-hidden rounded-md shadow-md">
                  <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="w-full transition-transform duration-700 group-hover:scale-[1.03] grayscale contrast-125 group-hover:grayscale-0" />
                  <div className="pointer-events-none absolute inset-0" style={{
              background: "radial-gradient(ellipse at center, transparent 60%, hsla(45,90%,5%,0.3) 100%)"
            }} />
                </div>
                <figcaption className="mt-3 flex items-baseline justify-between gap-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-base font-bold" style={{
                color: "var(--primary)"
              }}>
                      {romanNumerals[i]}
                    </span>
                    <span className="font-default text-foreground/85 text-xs font-semibold uppercase tracking-tight">
                      {image.caption}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{
              color: "var(--muted-foreground)"
            }}>
                    {image.year}
                  </span>
                </figcaption>
              </figure>)}
          </div>
        </div>}

      {}
      <Dialog open={lightboxIndex !== null} onOpenChange={open => !open && closeLightbox()}>
        <DialogContent showCloseButton={false} className="border-border/50 bg-background text-foreground h-[100dvh] w-[100vw] max-w-none translate-x-[-50%] translate-y-[-50%] gap-0 rounded-md border-0 p-0 sm:max-w-none">
          {lightboxIndex !== null && <div className="relative flex h-full w-full flex-col items-center justify-center px-4 py-8 md:px-20">
              <Button variant="ghost" size="icon" onClick={closeLightbox} aria-label="Close" className="text-foreground/70 hover:text-primary hover:bg-primary/10 absolute top-4 right-4 z-10 rounded-md md:top-6 md:right-6">
                <X className="size-5" />
              </Button>

              <Button variant="ghost" size="icon" onClick={prevImage} aria-label="Previous" className="hover:bg-primary/10 absolute left-2 top-1/2 z-10 size-12 -translate-y-1/2 rounded-md md:left-8" style={{
            color: "var(--primary)"
          }}>
                <ChevronLeft className="size-7" />
              </Button>

              <Button variant="ghost" size="icon" onClick={nextImage} aria-label="Next" className="hover:bg-primary/10 absolute right-2 top-1/2 z-10 size-12 -translate-y-1/2 rounded-md md:right-8" style={{
            color: "var(--primary)"
          }}>
                <ChevronRight className="size-7" />
              </Button>

              <figure className="flex max-h-full max-w-full flex-col items-center gap-5">
                <img src={galleryImages[lightboxIndex].src} data-wvc-srcset={galleryImages[lightboxIndex].srcset} data-wvc-sizes={galleryImages[lightboxIndex].sizes} alt={galleryImages[lightboxIndex].alt} className="max-h-[82vh] max-w-full rounded-md object-contain shadow-4xl contrast-110" />
                <figcaption className="flex flex-col items-center gap-2 text-center">
                  <span className="font-mono text-3xl font-bold" style={{
                color: "var(--primary)"
              }}>
                    {romanNumerals[lightboxIndex]}
                  </span>
                  <span className="font-default text-foreground text-xl font-bold uppercase tracking-tighter md:text-2xl">
                    {galleryImages[lightboxIndex].caption}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    {galleryImages[lightboxIndex].year} ·{" "}
                    {romanNumerals[lightboxIndex]} / {romanNumerals[galleryImages.length - 1]}
                  </span>
                </figcaption>
              </figure>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}